import React, { useState, useRef, useEffect, useCallback } from "react";
import "./LabelForm.scss";
import {
  getGlobalLabelsByID,
  saveGlobalLabel,
  updateGlobalLabel,
} from "../../services/api/LabelsApi.js";
import { useToast } from "../../context/ToastContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { WrittenFormat } from "../../components/WrittenFormat.js";
import { getBranchDetailsById } from "../../services/api/BranchApi.js";
import {
  getDistrictCasesByCourtRef,
  getHighCasesByCourtRef,
  getOtherCourtCasesAddedManuallyByCourtRef,
  getSupremeCourtCasesByCourtRef,
} from "../../services/api/CasesApi.js";
import { useGlobalSettings } from "../../context/GlobalSettingsContext.js";
import {
  updateLabelSettings,
  getAllGlobalSettingsData,
} from "../../services/api/GlobalSettingsApi.js";
import CommonModal from "../../components/CommonModal/CommonModal.js";


export const LabelForm = ({
  onSuccess,
  onCancel = null,
  initialData = null,
  isModal = false,
}) => {
  const { labelId } = useParams();
  const navigate = useNavigate();
  const { globalSettings, setGlobalSettings } = useGlobalSettings();
  const { company } = useAuth();
  const isEnt = company?.type === "ent";
  const { showToast } = useToast();


  // Basic form states
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [priorityLevel, setPriority] = useState("low");
  const [branchId, setBranchId] = useState("");
  const [color, setColor] = useState("#3B96CB");
  const [labelToEdit, setLabelToEdit] = useState(null);


  // Case selection states
  const [caseNumber, setCaseNumber] = useState("");
  const [LabelisDropdownOpen, setLabelIsDropdownOpen] = useState(false);
  const [LabelselectedCourtTab, setLabelSelectedCourtTab] = useState("high");
  const [LabelCases, setLabelCases] = useState([]);
  const [labelSearchTerm, setLabelSearchTerm] = useState("");
  const [labelFilteredCases, setLabelFilteredCases] = useState(LabelCases);
  const [selectedHighCourtCases, setSelectedHighCourtCases] = useState([]);
  const [selectedDistrictCourtCases, setSelectedDistrictCourtCases] = useState(
    []
  );
  const [selectedOtherCourtCases, setSelectedOtherCourtCases] = useState([]);
  const [selectedSupremeCourtCases, setSelectedSupremeCourtCases] = useState([]);


  // Color picker states
  const [isDragging, setIsDragging] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [pickerPosition, setPickerPosition] = useState({ x: 50, y: 50 });


  // Modal states
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [newPriorityValue, setNewPriorityValue] = useState("");
  const [isAddingNewPriority, setIsAddingNewPriority] = useState(false);


  // Other states
  const [branchesWithNames, setBranchesWithNames] = useState([]);
  const [isSaving, setIsSaving] = useState(false);


  // Refs
  const LabeldropdownRef = useRef();
  const labelInputRef = useRef(null);
  const gradientRef = useRef(null);
  const sliderRef = useRef(null);
  const modalRef = useRef(null);
  const nameInputRef = useRef(null);
  const hasFocusedRef = useRef(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  // MODIFICATION: Add a ref for the new priority modal
  const addPriorityModalRef = useRef(null);



  const [isLoadingCases, setIsLoadingCases] = useState(false);


  const handleCancelLabelForm = useCallback(() => {
    if (!isModal) {
      navigate("/home/labels");
    } else if (onCancel) {
      onCancel();
    }
  }, [isModal, navigate, onCancel]);


  // Handle click outside modal to close
  useEffect(() => {
    const handleModalClickOutside = (event) => {
      if (isModal && modalRef.current && !modalRef.current.contains(event.target)) {
        handleCancelLabelForm();
      }
    };


    if (isModal) {
      document.addEventListener('mousedown', handleModalClickOutside);
    }


    return () => {
      document.removeEventListener('mousedown', handleModalClickOutside);
    };
  }, [isModal, handleCancelLabelForm]);

  // MODIFICATION: Add useEffect to handle clicks outside the new "Add Priority" modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPriorityModal && addPriorityModalRef.current && !addPriorityModalRef.current.contains(event.target)) {
        setShowPriorityModal(false);
      }
    };

    if (showPriorityModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPriorityModal]);


  const isEdit = !!labelId || (!!initialData && !!initialData._id);
  const currentLabelId = labelId || initialData?._id;


  const priorityLabels = globalSettings?.result?.[0]?.labelSettings
    ?.priorityLevel || ["High", "Medium", "Low"];


  const COLOR_PALETTE = [
    "#FF6B6B",
    "#FF5722",
    "#F44336",
    "#E91E63",
    "#C62828",
    "#D32F2F",
    "#B71C1C",
    "#FF1744",
    "#FF9800",
    "#FF6F00",
    "#FF8F00",
    "#FFB74D",
    "#FB8C00",
    "#F57C00",
    "#EF6C00",
    "#E65100",
    "#FFEB3B",
    "#FFC107",
    "#FFD54F",
    "#FFCC02",
    "#FFB300",
    "#FFA000",
    "#FF8F00",
    "#FF6F00",
    "#4CAF50",
    "#8BC34A",
    "#CDDC39",
    "#009688",
    "#00BCD4",
    "#26A69A",
    "#66BB6A",
    "#81C784",
    "#2E7D32",
    "#388E3C",
    "#43A047",
    "#4CAF50",
    "#689F38",
    "#7CB342",
    "#8BC34A",
    "#9CCC65",
    "#2196F3",
    "#03A9F4",
    "#00BCD4",
    "#009688",
    "#4FC3F7",
    "#29B6F6",
    "#3B96CB",
    "#0288D1",
    "#0277BD",
    "#01579B",
    "#0D47A1",
    "#1565C0",
    "#1976D2",
    "#1E88E5",
    "#2196F3",
    "#42A5F5",
    "#9C27B0",
    "#673AB7",
    "#3F51B5",
    "#E91E63",
    "#AD1457",
    "#8E24AA",
    "#7B1FA2",
    "#6A1B9A",
    "#4A148C",
    "#512DA8",
    "#5E35B1",
    "#7986CB",
    "#9575CD",
    "#BA68C8",
    "#CE93D8",
    "#E1BEE7",
    "#607D8B",
    "#546E7A",
    "#455A64",
    "#37474F",
    "#263238",
    "#424242",
    "#616161",
    "#757575",
    "#9E9E9E",
    "#BDBDBD",
    "#E0E0E0",
    "#EEEEEE",
    "#F5F5F5",
    "#FAFAFA",
    "#000000",
    "#212121",
    "#FF4081",
    "#536DFE",
    "#448AFF",
    "#40C4FF",
    "#18FFFF",
    "#64FFDA",
    "#69F0AE",
    "#B2FF59",
    "#EEFF41",
    "#FFFF00",
    "#FFD740",
    "#FFAB40",
    "#FF6E40",
    "#FF5252",
    "#E040FB",
    "#7C4DFF",
  ];


  // Fetch global settings if not available
  useEffect(() => {
    const fetchGlobalSettings = async () => {
      if (
        !globalSettings ||
        !globalSettings.result ||
        !globalSettings.result[0]?.labelSettings
      ) {
        try {
          const response = await getAllGlobalSettingsData();
          if (response?.data) {
            setGlobalSettings(response.data);
          }
        } catch (error) {
          console.error("Failed to fetch global settings:", error);
        }
      }
    };
    fetchGlobalSettings();
  }, [globalSettings, setGlobalSettings]);


  // Fetch branch names
  useEffect(() => {
    const fetchBranchNames = async () => {
      if (!company?.branches || company.branches.length === 0) return;


      try {
        const fetchedBranches = await Promise.all(
          company.branches.map(async (id) => {
            const res = await getBranchDetailsById(id);
            return {
              id,
              name: res?.data?.result?.branchData?.branchName || id,
            };
          })
        );
        setBranchesWithNames(fetchedBranches);
      } catch (error) {
        console.error("Failed to fetch branch names", error);
      }
    };
    fetchBranchNames();
  }, [company?.branches]);


  // Fetch label data for editing
  useEffect(() => {
    const fetchLabelData = async () => {
      // If initialData is provided as prop, use it directly
      if (initialData) {
        setLabelToEdit(initialData);
        setName(initialData.name || "");
        setColor(initialData.color || "#3B96CB");
        setDescription(initialData.description || "");
        setPriority(
          initialData.priorityLevel
            ? WrittenFormat(initialData.priorityLevel)
            : ""
        );
        setCaseNumber(initialData.courtRef || "");
        setBranchId(initialData.branchId || "");
        setSelectedHighCourtCases(initialData.highCourtCases || []);
        setSelectedDistrictCourtCases(initialData.districtCourtCases || []);
        setSelectedOtherCourtCases(initialData.otherCourtCases || []);
        setInitialDataLoaded(true);





        const isNewLabelFromCaseDetails = initialData.courtRef &&
          !initialData._id &&
          (!initialData.highCourtCases || initialData.highCourtCases.length === 0) &&
          (!initialData.districtCourtCases || initialData.districtCourtCases.length === 0) &&
          (!initialData.otherCourtCases || initialData.otherCourtCases.length === 0);


        if (isNewLabelFromCaseDetails) {
          // Auto-detect court type and pre-select the case
          setTimeout(async () => {
            try {
              // Try to find the case in each court type
              const courtRef = initialData.courtRef;


              // Check High Court cases first
              try {
                const highResponse = await getHighCasesByCourtRef();
                const highCases = highResponse.data || [];
                const foundInHigh = highCases.find(caseItem => caseItem.courtRef === courtRef);
                if (foundInHigh) {
                  setLabelSelectedCourtTab("high");
                  setSelectedHighCourtCases([courtRef]);
                  return;
                }
              } catch (err) {
                console.error("Error checking high court cases:", err);
              }


              // Check District Court cases
              try {
                const districtResponse = await getDistrictCasesByCourtRef();
                const districtCases = districtResponse.data || [];
                const foundInDistrict = districtCases.find(caseItem => caseItem.courtRef === courtRef);
                if (foundInDistrict) {
                  setLabelSelectedCourtTab("district");
                  setSelectedDistrictCourtCases([courtRef]);
                  return;
                }
              } catch (err) {
                console.error("Error checking district court cases:", err);
              }


              // Check Other Court cases
              try {
                const otherResponse = await getOtherCourtCasesAddedManuallyByCourtRef();
                const otherCases = otherResponse.data || [];
                const foundInOther = otherCases.find(caseItem => caseItem.courtRef === courtRef);
                if (foundInOther) {
                  setLabelSelectedCourtTab("other");
                  setSelectedOtherCourtCases([courtRef]);
                  return;
                }
              } catch (err) {
                console.error("Error checking other court cases:", err);
              }


            } catch (error) {
              console.error("Error auto-detecting court type:", error);
            }
          }, 10); // Small delay to ensure component is fully mounted
        }
        return;
      }


      // Otherwise, fetch from API if labelId exists (route-based)
      if (labelId) {
        try {
          const response = await getGlobalLabelsByID(labelId);
          const fetchedLabel = response.data;
          setLabelToEdit(fetchedLabel);
          setName(fetchedLabel.name || "");
          setColor(fetchedLabel.color || "#3B96CB");
          setDescription(fetchedLabel.description || "");
          setPriority(
            fetchedLabel.priorityLevel
              ? WrittenFormat(fetchedLabel.priorityLevel)
              : ""
          );
          setCaseNumber(fetchedLabel.courtRef || "");
          setBranchId(fetchedLabel.branchId || "");
          setSelectedHighCourtCases(fetchedLabel.highCourtCases || []);
          setSelectedDistrictCourtCases(fetchedLabel.districtCourtCases || []);
          setSelectedOtherCourtCases(fetchedLabel.otherCourtCases || []);
          setInitialDataLoaded(true); // Mark as loaded


          // Set the default tab based on which court type has selected cases
          if (
            fetchedLabel.otherCourtCases &&
            fetchedLabel.otherCourtCases.length > 0
          ) {
            setLabelSelectedCourtTab("other");
          } else if (
            fetchedLabel.districtCourtCases &&
            fetchedLabel.districtCourtCases.length > 0
          ) {
            setLabelSelectedCourtTab("district");
          } else if (
            fetchedLabel.highCourtCases &&
            fetchedLabel.highCourtCases.length > 0
          ) {
            setLabelSelectedCourtTab("high");
          }
        } catch (error) {
          console.error("Failed to fetch label data:", error);
          showToast("Failed to load label data.", "error");
        }
      }
      else{
        setInitialDataLoaded(true); 
      }
    };
    fetchLabelData();
  }, [labelId, initialData]);


  // Fetch cases based on selected court tab
  // useEffect(() => {
  //   const fetchLabelCases = async () => {
  //     try {
  //       let response;
  //       if (LabelselectedCourtTab === "high") {
  //         response = await getHighCasesByCourtRef();
  //       } else if (LabelselectedCourtTab === "district") {
  //         response = await getDistrictCasesByCourtRef();
  //       } else if (LabelselectedCourtTab === "other") {
  //         response = await getOtherCourtCasesAddedManuallyByCourtRef();
  //       }
  //       setLabelCases(response.data || []);
  //     } catch (err) {
  //       console.error("Error fetching cases:", err);
  //     }
  //   };
  //   fetchLabelCases();
  // }, [LabelselectedCourtTab]);


  useEffect(() => {
    const fetchLabelCases = async () => {
      setIsLoadingCases(true); // Start loading
      try {
        let response;
        if (LabelselectedCourtTab === "high") {
          response = await getHighCasesByCourtRef();
        } else if (LabelselectedCourtTab === "district") {
          response = await getDistrictCasesByCourtRef();
        } else if (LabelselectedCourtTab === "other") {
          response = await getOtherCourtCasesAddedManuallyByCourtRef();
        }
        setLabelCases(response.data || []);
      } catch (err) {
        console.error("Error fetching cases:", err);
        setLabelCases([]); // Set empty array on error
      } finally {
        setIsLoadingCases(false); // Stop loading
      }
    };
    fetchLabelCases();
  }, [LabelselectedCourtTab]);


  // Filter cases based on search term
  useEffect(() => {
    // Ensure labelFilteredCases is always an array
    let filtered = Array.isArray(LabelCases) ? LabelCases : [];


    if (labelSearchTerm.trim()) {
      filtered = filtered.filter(
        (caseItem) =>
          caseItem.parties
            ?.toLowerCase()
            .includes(labelSearchTerm.toLowerCase()) ||
          caseItem.courtRef
            ?.toLowerCase()
            .includes(labelSearchTerm.toLowerCase()) ||
          caseItem.caseDate?.caseTypeAndNumber
            ?.toLowerCase()
            .includes(labelSearchTerm.toLowerCase())
      );
    }


    setLabelFilteredCases(filtered);
  }, [labelSearchTerm, LabelCases, LabelselectedCourtTab]);


  // Click outside handler for dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        LabeldropdownRef.current &&
        !LabeldropdownRef.current.contains(event.target)
      ) {
        setLabelIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  // Color picker effects
  useEffect(() => {
    if (color && color.startsWith("#")) {
      const [h, s, l] = hexToHsl(color);
      setSliderPosition((h / 360) * 100);
      setPickerPosition({ x: s, y: 100 - l });
    }
  }, [color]);


  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      if (
        e.target === gradientRef.current ||
        e.target.closest(".color-gradient")
      ) {
        updatePickerPosition(e);
      } else if (
        e.target === sliderRef.current ||
        e.target.closest(".color-slider")
      ) {
        updateSliderPosition(e);
      }
    };


    const handleMouseUp = () => {
      setIsDragging(false);
    };


    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }


    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);
  useEffect(() => {
    // Prevent multiple focus attempts
    if (hasFocusedRef.current) return;

    // For editing mode, wait for initial data to load
    // For new label, focus immediately after component mounts
    const shouldFocus = isEdit ? initialDataLoaded : true;
    
    if (!shouldFocus) return;

    // Add a small delay to ensure DOM is ready
    const focusTimeout = setTimeout(() => {
      const nameInput = nameInputRef.current;
      if (nameInput && !hasFocusedRef.current) {
        nameInput.focus();
        
        // Move cursor to end of existing text (useful for edit mode)
        const val = nameInput.value || '';
        try {
          nameInput.setSelectionRange(val.length, val.length);
        } catch (e) {
          // Ignore selection range errors
        }
        
        hasFocusedRef.current = true;
      }
    }, 200);

    return () => clearTimeout(focusTimeout);
  }, [initialDataLoaded, isEdit]);


  const handleFormKeyDown = (e) => {
    // Check if Enter was pressed and form is valid
    if (e.key === 'Enter' && !e.shiftKey) {
      // Don't submit if we're in certain elements that need Enter for their functionality
      const target = e.target;
      const tagName = target.tagName.toLowerCase();
      const isSelect = tagName === 'select';
      const isTextarea = tagName === 'textarea';
      const isInDropdown = target.closest('.dropdown-content');
      const isInModal = target.closest('.modal') || target.closest('[class*="modal"]');
      const isInCaseSelector = target.closest('.case-selector');
      const isColorPicker = target.closest('.color-picker') || target.closest('.color-palette');
      
      if (isSelect || isTextarea || isInDropdown || isInModal || (isInCaseSelector && LabelisDropdownOpen) || isColorPicker) {
        return;
      }
      
      e.preventDefault();
      
      if (isFormValid() && !isSaving) {
        handleSave();
      }
    }
  };

  const handleModalKeyDown = (e) => {
    if (e.key === 'Enter' && newPriorityValue.trim() && !isAddingNewPriority) {
      e.preventDefault();
      handleAddNewPriorityConfirm();
    }
  };

  // Reset focus ref when form is cleared (add this to your handleSave after successful save)
  const resetFocusRef = () => {
    hasFocusedRef.current = false;
  };


  const getLabelDisplayValue = () => {
    if (labelSearchTerm && LabelisDropdownOpen) {
      return labelSearchTerm;
    }


    // Ensure LabelCases is an array before using .find()
    const casesArray = Array.isArray(LabelCases) ? LabelCases : [];
    const selected = casesArray.find(
      (item) => String(item.courtRef).trim() === String(caseNumber).trim()
    );
    return selected ? `${selected.courtRef}` : "";
  };
  const handleLabelInputChange = (e) => {
    const value = e.target.value;
    setLabelSearchTerm(value);
    setLabelIsDropdownOpen(true);
    if (value !== getLabelDisplayValue()) {
      setCaseNumber("");
    }
  };


  const handleLabelInputFocus = () => {
    setLabelIsDropdownOpen(true);
    setLabelSearchTerm("");
  };


  const handleLabelCaseSelect = (caseItem) => {
    const ref = caseItem.courtRef;
    if (LabelselectedCourtTab === "high") {
      setSelectedHighCourtCases((prev) =>
        prev.includes(ref) ? prev.filter((r) => r !== ref) : [...prev, ref]
      );
    } else if (LabelselectedCourtTab === "district") {
      setSelectedDistrictCourtCases((prev) =>
        prev.includes(ref) ? prev.filter((r) => r !== ref) : [...prev, ref]
      );
    } else if (LabelselectedCourtTab === "other") {
      setSelectedOtherCourtCases((prev) =>
        prev.includes(ref) ? prev.filter((r) => r !== ref) : [...prev, ref]
      );
    }
  };


  const handleLabelKeyDown = (e) => {
    if (e.key === "Escape") {
      setLabelIsDropdownOpen(false);
      labelInputRef.current?.blur();
    } else if (e.key === "Enter" && Array.isArray(labelFilteredCases) && labelFilteredCases.length === 1) {
      handleLabelCaseSelect(labelFilteredCases[0]);
    }
  };
  useEffect(() => {
    if (!isModal) return;

    const handleDocumentKeyDown = (e) => {
      // Only handle if the event originated from within our form container
      if (e.target.closest('.label-form-container') && modalRef.current?.contains(e.target)) {
        handleFormKeyDown(e);
      }
    };

    document.addEventListener('keydown', handleDocumentKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [isModal, isSaving, LabelisDropdownOpen]);


  // Color picker functions
  const updatePickerPosition = (e) => {
    if (gradientRef.current) {
      const rect = gradientRef.current.getBoundingClientRect();
      const x = Math.max(
        0,
        Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)
      );
      const y = Math.max(
        0,
        Math.min(100, ((e.clientY - rect.top) / rect.height) * 100)
      );
      setPickerPosition({ x, y });


      const hue = 360 * (sliderPosition / 100);
      const saturation = x;
      const lightness = 100 - y;
      const updatedColor = hslToHex(hue, saturation, lightness);
      setColor(updatedColor);
    }
  };


  const updateSliderPosition = (e) => {
    if (sliderRef.current) {
      const rect = sliderRef.current.getBoundingClientRect();
      const position = Math.max(
        0,
        Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)
      );
      setSliderPosition(position);


      const hue = 360 * (position / 100);
      const saturation = pickerPosition.x;
      const lightness = 100 - pickerPosition.y;
      const updatedColor = hslToHex(hue, saturation, lightness);
      setColor(updatedColor);
    }
  };


  const hslToHex = (h, s, l) => {
    l /= 100;
    const a = (s * Math.min(l, 1 - l)) / 100;
    const f = (n) => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color)
        .toString(16)
        .padStart(2, "0");
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  };


  const hexToHsl = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;


    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h,
      s,
      l = (max + min) / 2;


    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }
    return [h * 360, s * 100, l * 100];
  };


  const handleGradientMouseDown = (e) => {
    setIsDragging(true);
    updatePickerPosition(e);
  };


  const handleSliderMouseDown = (e) => {
    setIsDragging(true);
    updateSliderPosition(e);
  };


  // Priority modal functions
  const handleAddNewPriorityConfirm = async () => {
    if (!newPriorityValue.trim()) return;


    setIsAddingNewPriority(true);


    try {
      const currentLabelSettings =
        globalSettings?.result?.[0]?.labelSettings || {};
      const existingPriorityLevels = currentLabelSettings.priorityLevel;
      const newPriorityUpperCase = newPriorityValue.trim().toUpperCase();


      if (existingPriorityLevels.includes(newPriorityUpperCase)) {
        showToast(
          `${newPriorityUpperCase} already exists in priority levels`,
          "info"
        );
        setIsAddingNewPriority(false);
        return;
      }


      const updatedPriorityLevels = [
        ...existingPriorityLevels,
        newPriorityUpperCase,
      ];
      const payload = { priorityLevel: updatedPriorityLevels };


      await updateLabelSettings(payload);
      showToast(`New priority level added successfully!`, "success");


      const updatedSettings = {
        ...globalSettings,
        result: globalSettings?.result?.map((setting) => ({
          ...setting,
          labelSettings: {
            ...setting.labelSettings,
            priorityLevel: updatedPriorityLevels,
          },
        })),
      };


      setGlobalSettings(updatedSettings);
      setPriority(newPriorityValue.trim().toLowerCase());
      setNewPriorityValue("");
      setShowPriorityModal(false);
    } catch (err) {
      console.error("Failed to add priority level", err);
      showToast("Failed to add new priority level.", "error");
    } finally {
      setIsAddingNewPriority(false);
    }
  };


  // Form validation
  const isFormValid = () => {
    if (!name || name.trim() === "") return false;
    if (!description || description.trim() === "") return false;
    if (!color || color.trim() === "") return false;
    if (!priorityLevel || priorityLevel.trim() === "") return false;
    // if (isEnt && (!branchId || branchId.trim() === "")) return false;


    // const hasSelectedCases =
    //   selectedHighCourtCases.length > 0 ||
    //   selectedDistrictCourtCases.length > 0 ||
    //   selectedOtherCourtCases.length > 0;
    // if (!hasSelectedCases) return false;


    return true;
  };




  // Save handler
  const handleSave = async () => {
    if (!isFormValid()) {
      showToast(
        "Please fill in all required fields and select at least one case.",
        "error"
      );
      return;
    }


    if (isSaving) return;
    setIsSaving(true);


    const labelData = {
      name,
      description,
      color,
      priorityLevel: priorityLevel.toUpperCase(),
      ...(isEnt && { branchId }),
      highCourtCases: selectedHighCourtCases,
      districtCourtCases: selectedDistrictCourtCases,
      otherCourtCases: selectedOtherCourtCases,
    };


    try {
      if (isEdit) {
        await updateGlobalLabel(currentLabelId, labelData);
      } else {
        await saveGlobalLabel(labelData);
      }


      setCaseNumber("");
      setName("");
      setDescription("");
      setBranchId("");
      setPriority("");
      setSelectedHighCourtCases([]);
      setSelectedDistrictCourtCases([]);
      setSelectedOtherCourtCases([]);


      showToast(
        `Label ${isEdit ? "updated" : "saved"} successfully!`,
        "success"
      );
      onSuccess && onSuccess();


      // Remove the navigation - only navigate if not in modal mode
      if (!isModal) {
        navigate("/home/settings");
      }
    } catch (error) {
      console.error("Failed to save or update label:", error);
      showToast(`Failed to ${isEdit ? "update" : "save"} label`, "error");
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <div
      className={`label-form-container ${isModal ? "modal-label-form-container" : ""
        }`}
      ref={modalRef}
      onKeyDown={handleFormKeyDown}
    >
      <div
        className={`label-form-card ${isModal ? "modal-label-form-card" : ""}`}
      >
        <h1
          className={`label-form-title ${isModal ? "modal-label-form-title" : ""
            }`}
        >
          {isEdit ? "Update Label" : "Create New Label"}
        </h1>


        {isModal && (
          <button
            className="label-form-close-button"
            onClick={handleCancelLabelForm}
            aria-label="Close"
          >
            &times;
          </button>
        )}


        <hr className="label-form-divider" />


        {/* Basic Information Section */}
        <div className="form-section">
          <h3>Basic Information</h3>

          <div className="label-form-flex-row">
            <div className="label-form-col-half">
              <label className="label-form-label">
                Name <span className="label-form-required">*</span>
              </label>
              <input
                ref={nameInputRef}
                type="text"
                placeholder="Enter Label Name"
                className="label-form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>


            <div className="label-form-col-half">
              <label className="label-form-label">
                Description <span className="label-form-required">*</span>
              </label>
              <input
                type="text"
                placeholder="Short description"
                className="label-form-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>


          <div className="label-form-flex-row">
            <div className="label-form-col-half">
              <label className="label-form-label">
                Priority Level <span className="label-form-required">*</span>
              </label>
              <select
                className="label-form-form-select"
                value={priorityLevel}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "__add_new__") {
                    setShowPriorityModal(true);
                  } else {
                    setPriority(val);
                  }
                }}
              >
                <option value="">Select Priority</option>
                {priorityLabels && priorityLabels.length > 0 ? (
                  priorityLabels.map((level) => (
                    <option key={level} value={level.toLowerCase()}>
                      {level.charAt(0) + level.slice(1).toLowerCase()}
                    </option>
                  ))
                ) : (
                  <>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </>
                )}
                <option value="__add_new__">+ Add New Priority Level</option>
              </select>
            </div>


            <div className="label-form-col-half">
              <label className="label-form-label">
                Color Code <span className="label-form-required">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter color code (e.g., #3B96CB)"
                className="label-form-input"
                value={color}
                onChange={(e) => setColor(e.target.value)}
              />
            </div>
          </div>
        </div>


        {/* Case Association Section */}
        <div className="form-section">
          <h3>Case Association</h3>


          <div className="form-group">
            <div className="case-selector" ref={LabeldropdownRef}>
              <label className="label-form-label">
                Select Cases
              </label>
              <input
                ref={labelInputRef}
                type="text"
                value={getLabelDisplayValue()}
                onChange={handleLabelInputChange}
                onFocus={handleLabelInputFocus}
                onKeyDown={handleLabelKeyDown}
                className="custom-dropdown-input"
                placeholder="Type to search cases..."
                autoComplete="off"
              />


              {(selectedHighCourtCases.length > 0 ||
                selectedDistrictCourtCases.length > 0 ||
                selectedOtherCourtCases.length > 0) && (
                  <div className="selected-cases-tags">
                    {selectedHighCourtCases.map((ref, i) => (
                      <span key={`high-${i}`} className="case-tag high-court">
                        {ref}
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedHighCourtCases((prev) =>
                              prev.filter((r) => r !== ref)
                            )
                          }
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    {selectedDistrictCourtCases.map((ref, i) => (
                      <span
                        key={`district-${i}`}
                        className="case-tag district-court"
                      >
                        {ref}
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedDistrictCourtCases((prev) =>
                              prev.filter((r) => r !== ref)
                            )
                          }
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    {selectedOtherCourtCases.map((ref, i) => (
                      <span key={`other-${i}`} className="case-tag other-court">
                        {ref}
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedOtherCourtCases((prev) =>
                              prev.filter((r) => r !== ref)
                            )
                          }
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}


              {LabelisDropdownOpen && (
                <div className="dropdown-content">
                  <div className="dropdown-tabs">
                    <button
                      type="button"
                      className={
                        LabelselectedCourtTab === "high" ? "active" : ""
                      }
                      onClick={() => setLabelSelectedCourtTab("high")}
                    >
                      High Court
                    </button>
                    <button
                      type="button"
                      className={
                        LabelselectedCourtTab === "district" ? "active" : ""
                      }
                      onClick={() => setLabelSelectedCourtTab("district")}
                    >
                      District Court
                    </button>
                    <button
                      type="button"
                      className={
                        LabelselectedCourtTab === "other" ? "active" : ""
                      }
                      onClick={() => setLabelSelectedCourtTab("other")}
                    >
                      Other Court
                    </button>
                  </div>


                  {/* <div className="dropdown-options">
                    {labelSearchTerm &&
                      (!Array.isArray(labelFilteredCases) ||
                        labelFilteredCases.length === 0) &&
                      Array.isArray(LabelCases) &&
                      LabelCases.length > 0 && (
                        <div className="no-data">
                          No cases found matching "{labelSearchTerm}"
                        </div>
                      )}


                    {(!Array.isArray(labelFilteredCases) ||
                      labelFilteredCases.length === 0) &&
                      !labelSearchTerm ? (
                      <div className="no-data">No cases found.</div>
                    ) : (
                      Array.isArray(labelFilteredCases) &&
                      labelFilteredCases.map((caseItem, index) => (
                        <div
                          key={index}
                          className={`dropdown-option ${(
                              LabelselectedCourtTab === "high"
                                ? selectedHighCourtCases.includes(
                                  caseItem.courtRef
                                )
                                : LabelselectedCourtTab === "district"
                                  ? selectedDistrictCourtCases.includes(
                                    caseItem.courtRef
                                  )
                                  : selectedOtherCourtCases.includes(
                                    caseItem.courtRef
                                  )
                            )
                              ? "selected"
                              : ""
                            }`}
                          onClick={() => handleLabelCaseSelect(caseItem)}
                        >
                          <div className="case-info">
                            <div className="case-parties">
                              {caseItem.parties || "No parties info"}
                            </div>
                            <div className="case-ref">{caseItem.courtRef}</div>
                            {caseItem.caseDate?.caseTypeAndNumber && (
                              <div className="case-number">
                                {caseItem.caseDate.caseTypeAndNumber}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div> */}
                  <div className="dropdown-options">
                    {isLoadingCases ? (
                      <div className="loading-state">Loading cases...</div>
                    ) : (
                      <>
                        {labelSearchTerm &&
                          (!Array.isArray(labelFilteredCases) ||
                            labelFilteredCases.length === 0) &&
                          Array.isArray(LabelCases) &&
                          LabelCases.length > 0 && (
                            <div className="no-data">
                              No cases found matching "{labelSearchTerm}"
                            </div>
                          )}


                        {(!Array.isArray(labelFilteredCases) ||
                          labelFilteredCases.length === 0) &&
                          !labelSearchTerm ? (
                          <div className="no-data">
                            No {LabelselectedCourtTab} court cases available
                          </div>
                        ) : (
                          Array.isArray(labelFilteredCases) &&
                          labelFilteredCases.map((caseItem, index) => (
                            <div
                              key={index}
                              className={`dropdown-option ${(
                                LabelselectedCourtTab === "high"
                                  ? selectedHighCourtCases.includes(
                                    caseItem.courtRef
                                  )
                                  : LabelselectedCourtTab === "district"
                                    ? selectedDistrictCourtCases.includes(
                                      caseItem.courtRef
                                    )
                                    : selectedOtherCourtCases.includes(
                                      caseItem.courtRef
                                    )
                              )
                                ? "selected"
                                : ""
                                }`}
                              onClick={() => handleLabelCaseSelect(caseItem)}
                            >
                              <div className="case-info">
                                <div className="case-parties">
                                  {caseItem.parties || "No parties info"}
                                </div>
                                <div className="case-ref">{caseItem.courtRef}</div>
                                {caseItem.caseDate?.caseTypeAndNumber && (
                                  <div className="case-number">
                                    {caseItem.caseDate.caseTypeAndNumber}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>


        {/* Branch Selection (Enterprise Only) */}
        {isEnt && (
          <div className="form-section">
            <h3>Branch Information</h3>


            <div className="label-form-group">
              <label className="label-form-label">
                Branch
              </label>
              <select
                className="label-form-form-select"
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                disabled={isEdit}
              >
                <option value="">Select Branch</option>
                {branchesWithNames.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}


        {/* Color Selection Section */}
        <div className="form-section">
          <h3>Color Selection</h3>


          <div className="label-form-group color-palette-container">
            <label className="label-form-label">
              Choose from Color Palette
            </label>
            <div className="color-palette">
              {COLOR_PALETTE.map((paletteColor, index) => (
                <div
                  key={index}
                  className={`color-circle ${color === paletteColor ? "selected" : ""
                    }`}
                  style={{ backgroundColor: paletteColor }}
                  onClick={() => setColor(paletteColor)}
                  title={paletteColor}
                />
              ))}
            </div>
            <div className="selected-color-display">
              <span>Selected: </span>
              <div
                className="selected-color-preview"
                style={{ backgroundColor: color }}
              />
              <span className="selected-color-code">{color}</span>
            </div>
          </div>


          {/* Advanced Color Picker */}
          <div className="label-form-group">
            <label className="label-form-label">Advanced Color Picker</label>
            <div className="color-picker">
              <div
                className="color-gradient"
                ref={gradientRef}
                onMouseDown={handleGradientMouseDown}
                style={{
                  background: `linear-gradient(to bottom,
                    transparent,
                    black),
                    linear-gradient(to right,
                    white,
                    hsl(${360 * (sliderPosition / 100)}, 100%, 50%))`,
                }}
              >
                <div
                  className="selector-circle"
                  style={{
                    left: `${pickerPosition.x}%`,
                    top: `${pickerPosition.y}%`,
                  }}
                />
              </div>


              <div className="color-controls">
                <div
                  className="color-circle"
                  style={{ backgroundColor: color }}
                />
                <div
                  className="color-slider"
                  ref={sliderRef}
                  onMouseDown={handleSliderMouseDown}
                >
                  <div
                    className="slider-handle"
                    style={{ left: `${sliderPosition}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Form Actions */}
        <div className="label-form-button-container">
          <button
            onClick={handleSave}
            className="label-form-btn label-form-btn-save"
            disabled={!isFormValid() || isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
          <button
            onClick={handleCancelLabelForm}
            className="label-form-btn-cancel"
            disabled={isSaving}
          >
            Cancel
          </button>
        </div>
      </div>


      {/* Priority Modal */}
      {/* MODIFICATION: Replaced CommonModal with a new structure */}
      {showPriorityModal && (
        <div className="confirmation-modal-backdrop">
          <div className="confirmation-modal notinvoice" ref={addPriorityModalRef}>
            <button
              className="label-form-close-button"
              onClick={() => setShowPriorityModal(false)}
              aria-label="Close"
            >
              &times;
            </button>

            <div className="add-new-field-modal-content">
              <h3>Add New Priority Level</h3>
              <input
                type="text"
                placeholder="Enter new priority level"
                value={newPriorityValue}
                onChange={(e) => setNewPriorityValue(e.target.value)}
                className="add-new-field-input"
                onKeyDown={handleModalKeyDown}
                autoFocus
              />
            </div>
            
            <div className="confirmation-actions notinvoice">
              {/* <button
                className="cancel-btn"
                onClick={() => setShowPriorityModal(false)}
                disabled={isAddingNewPriority}
              >
                Cancel
              </button> */}
              <button
                className="confirm-btn"
                onClick={handleAddNewPriorityConfirm}
                disabled={!newPriorityValue.trim() || isAddingNewPriority}
              >
                {isAddingNewPriority ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};